import React, { forwardRef } from "react";

import type { SvgIconProps } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import Divider, { DividerProps } from "@mui/material/Divider";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";
import Tab, { TabProps } from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import useMeasure from "@app/common/hooks/useMeasure";

import { Row, StackProps } from "@rui/Stack";

export const FloatingNavTab = styled<React.FC<TabProps>>((props) => (
  <Tab {...props} />
))(({ theme }) => ({
  borderRadius: theme.corners.base,
  fontSize: "0.875rem",
  minHeight: "32px",
  minWidth: "fit-content",
  height: "32px",
  padding: theme.spaces.small,
  color: theme.palette.shades.neutral[600],

  "&:hover": {
    backgroundColor: theme.palette.shades.primary[50],
    color: theme.palette.shades.neutral[800],
  },

  "&.Mui-selected": {
    backgroundColor: theme.palette.overlay.primary[12],
    color: "#FFF",
  },
  [theme.breakpoints.up("sm")]: {
    "&:not(:last-child)": {
      marginRight: "4px",
    },
  },
  [theme.breakpoints.up("mobile")]: {
    padding: "6px 12px",
  },
}));

export const FloatingNavTabs = styled(Tabs)({
  minHeight: "32px",
  height: "32px",
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "&.hidden": {
    display: "none",
  },
});

export const FloatingNavActions = (props: StackProps) => (
  <Row variant="right" spacing={1} {...props} />
);

export const FloatingNavActionDivider = styled<React.FC<DividerProps>>(
  (props) => <Divider orientation="vertical" {...props} />
)(({ theme }) => ({
  height: "18px",
  color: theme.palette.shades.neutral[100],
}));

type FloatingNavButtonProps = ButtonProps & {
  active?: boolean;
  Icon: React.FunctionComponent<SvgIconProps>;
};

type FloatingNavIconButtonProps = IconButtonProps & {
  Icon: React.FunctionComponent<SvgIconProps>;
};

export const FloatingNavIconButton = styled<
  React.FC<FloatingNavIconButtonProps>
>(({ Icon, ...props }) => (
  <IconButton {...props}>
    <Icon />
  </IconButton>
))(({ theme }) => ({
  color: theme.palette.shades.neutral[300],
  borderRadius: theme.corners.base,
  "&:hover": {
    backgroundColor: theme.palette.shades.ghost[500],
    color: theme.palette.shades.primary[500],
  },
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
  },
}));

const FloatingNavButtonComponent = styled<React.FC<FloatingNavButtonProps>>(
  ({ Icon, children, active: _a, ...props }) => (
    <Button startIcon={<Icon />} {...props}>
      <span className="floating-nav__button__text">{children}</span>
    </Button>
  )
)(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
  minWidth: "fit-content",
  fontSize: "0.75rem",
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
    svg: {
      transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      fontSize: "16px",
      color: theme.palette.shades.neutral[300],
    },
  },
  "& .floating-nav__button__text": {
    marginLeft: "6px",
  },
  "&:hover": {
    color: theme.palette.shades.primary[500],
    "& .MuiButton-startIcon svg": {
      color: theme.palette.shades.primary[500],
    },
  },
}));

export const FloatingNavButton = (props: FloatingNavButtonProps) => (
  <FloatingNavButtonComponent {...props} className="floating-nav__button" />
);

export type FloatingNavProps = StackProps;

/**
 * Not essential for basic styling purposes; relatively positioned container that can
 * be nested inside the floating nav so as not to interfere with the sticky positioning
 * of the outer container
 * */
export const FloatingNavInnerContainer = styled<React.FC<StackProps>>(
  (props) => <Row variant="apart" {...props} />
)({
  height: "100%",
  width: "100%",
  position: "relative",
});

const FloatingNavContainer = styled(Row)(({ theme }) => ({
  width: "100%",
  maxWidth: theme.breakpoints.values.xl,
  position: "sticky",
  top: theme.spaces.small,
  paddingLeft: theme.spaces.small,
  paddingRight: theme.spaces.small,
  paddingBottom: theme.spaces.small,
  zIndex: 10,
  minHeight: "56px",
  alignSelf: "center",
  [theme.breakpoints.up("mobile")]: {
    top: theme.spaces.base,
    paddingLeft: theme.spaces.base,
    paddingRight: theme.spaces.base,
  },
}));

const FloatingNavWithRef = forwardRef<HTMLDivElement | null, FloatingNavProps>(
  (props, ref) => <Row variant="apart" ref={ref} {...props} />
);

FloatingNavWithRef.displayName = "FloatingNavWithRef";

const FloatingNav = styled(FloatingNavWithRef)(({ theme }) => ({
  width: "100%",
  minHeight: "48px",
  height: "48px",
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
  backgroundColor: "#FFF",
  border: `2px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: theme.corners.base,
  padding: theme.spaces.xsmall,
  "&.truncated": {
    ".floating-nav__button .MuiButton-startIcon svg": {
      fontSize: "20px",
    },
    ".floating-nav__button__text": {
      display: "none",
    },
  },
}));

type ResponsiveFloatingNavProps = FloatingNavProps & { breakpoint?: number };

const ResponsiveFloatingNav = ({
  breakpoint = 0,
  ...props
}: ResponsiveFloatingNavProps) => {
  const [navRef, , { innerWidth }] = useMeasure();

  return (
    <FloatingNavContainer>
      <FloatingNav
        ref={navRef}
        {...props}
        className={
          breakpoint > 0 && innerWidth <= breakpoint ? "truncated" : undefined
        }
      />
    </FloatingNavContainer>
  );
};

export default ResponsiveFloatingNav;
